import * as UI from "@iyk/ui"
import * as SheetPrimitive from "@radix-ui/react-dialog"
import * as React from "react"

import { HlsVideo } from "../../../packages/hls-video.tsx"
import { ExclusiveContainer } from "./exclusive-container.tsx"
import { useExclusiveSheet } from "./use-exclusive-sheet.ts"

const TIMER_DURATION = 3000

// #region Video Card

export const ExclusiveVideoCard = (props: ExclusiveVideoCard.Props) => {
  const [open, setOpen] = useExclusiveSheet(props.id)

  const pauseOtherMedia = () => {
    const audioElements = document.querySelectorAll("audio")
    audioElements.forEach((audio) => {
      if (!audio.paused) {
        audio.pause()
      }
    })
  }

  const onOpenChange = (open: boolean) => {
    setOpen(open)
    if (open) {
      pauseOtherMedia()
    }
  }

  return (
    <Sheet.Root open={open} onOpenChange={onOpenChange}>
      <Sheet.Trigger>
        <ExclusiveContainer>
          <div className="absolute inset-0 size-full">
            <img src={props.coverImage} alt={props.title} className="size-full object-cover" />
            <div className="absolute bottom-0 left-0 w-full h-1/4 bg-[linear-gradient(to_top,_black,_rgba(0,0,0,0))]" />
          </div>
          <div className="absolute right-2 bottom-2">
            <span className={UI.classForButton({ kind: "solid", size: "sm" })}>
              <UI.Icon.Play />
            </span>
          </div>

          <div className="absolute left-2 bottom-2 text-xs text-start">
            <h3>{props.title}</h3>
            <p className="mt-0.5 text-gray-11">{props.subtitle}</p>
          </div>
        </ExclusiveContainer>
      </Sheet.Trigger>
      <Sheet.Content title={props.title} description={props.subtitle}>
        <div className="h-dvh relative flex flex-col gap-4 justify-center items-center">
          <VideoPlayer
            id={props.id}
            title={props.title}
            coverImage={props.coverImage}
            videoSrc={props.videoSrc}
            onPlay={props.onPlay}
            subtitle={props.subtitle}
          />
          <UI.Button
            type="button"
            kind="shaded"
            size="md"
            className="absolute right-2 top-2"
            onClick={() => setOpen(false)}
          >
            <UI.Icon.Cross />
          </UI.Button>
        </div>
      </Sheet.Content>
    </Sheet.Root>
  )
}

namespace ExclusiveVideoCard {
  export type Props = {
    id: string
    title: string
    subtitle?: string
    coverImage: string
    videoSrc: string
    onPlay: () => void
  }
}

// #endregion

// #region Video Player

const VideoPlayer = (props: ExclusiveVideoCard.Props) => {
  const [isPlaying, setIsPlaying] = React.useState(false)
  const [showButton, setShowButton] = React.useState(true)
  const videoRef = React.useRef<HTMLVideoElement>(null)
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  const togglePlay = () => {
    const video = videoRef.current
    if (!video) return

    if (video.paused) {
      video.play()
      props.onPlay()
    } else {
      video.pause()
    }
    setIsPlaying(!video.paused)
  }

  const onVideoClick = (e: React.MouseEvent<HTMLVideoElement>) => {
    const isButtonClick = (e.target as HTMLElement).closest(".play-pause-button")
    if (isButtonClick) {
      togglePlay()
    } else if (isPlaying) {
      setShowButton((prev) => !prev)
    }
  }

  React.useEffect(() => {
    if (!isPlaying || !showButton) return

    timeoutRef.current = setTimeout(() => {
      setShowButton(false)
    }, TIMER_DURATION)

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [isPlaying, showButton])

  React.useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const controller = new AbortController()

    const handleEnded = () => {
      setIsPlaying(false)
      setShowButton(true)
    }

    video.addEventListener("ended", handleEnded, { signal: controller.signal })
    return () => controller.abort()
  }, [])

  return (
    <div className="relative w-full aspect-video">
      <HlsVideo
        ref={videoRef}
        autoPlay={false}
        poster={props.coverImage}
        onClick={onVideoClick}
        src={props.videoSrc}
        className="aspect-video size-full object-cover"
      />
      <button
        onClick={togglePlay}
        className={UI.cx([
          "play-pause-button absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2",
          "flex items-center justify-center text-white rounded-full bg-gray-5",
          "p-3 transition-opacity duration-300",
          showButton ? "opacity-100" : "opacity-0",
        ])}
      >
        {isPlaying ? <UI.Icon.Pause /> : <UI.Icon.Play />}
      </button>
    </div>
  )
}

// #endregion

// #region Video Card Sheet

const SheetRoot = SheetPrimitive.Root

const SheetTrigger = SheetPrimitive.Trigger

const SheetPortal = SheetPrimitive.Portal

const SheetOverlay = () => <SheetPrimitive.Overlay className="fixed inset-0 z-50 bg-transparent" />

const sheetContentClasses = UI.cx([
  "fixed max-w-[var(--app-max-width)] mx-auto z-50 gap-4 bg-background shadow-lg transition ease-in-out text-foreground",
  "data-[state=open]:animate-in data-[state=closed]:animate-out",
  "data-[state=closed]:duration-500 data-[state=open]:duration-500",
  "inset-x-0 top-0",
  "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
])

const SheetContent = ({ children, title, description, ...props }: Sheet.ContentProps) => (
  <SheetPortal>
    <SheetOverlay />
    <SheetPrimitive.Content
      className={sheetContentClasses}
      onOpenAutoFocus={(e) => e.preventDefault()}
      {...props}
    >
      <div className="sr-only">
        <SheetPrimitive.Title>{title}</SheetPrimitive.Title>
        {description && <SheetPrimitive.Description>{description}</SheetPrimitive.Description>}
      </div>
      {children}
    </SheetPrimitive.Content>
  </SheetPortal>
)

namespace Sheet {
  export type ContentProps = {
    title: string
    description?: string
    children: React.ReactNode
  } & SheetPrimitive.DialogContentProps
}

const Sheet = {
  Root: SheetRoot,
  Content: SheetContent,
  Overlay: SheetOverlay,
  Portal: SheetPortal,
  Trigger: SheetTrigger,
}

// #endregion
